import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import type { ImperativeDialogRef } from "~/hooks/use-imperative-dialog";
import {
  useImperativeDialog,
  useImperativeDialogRef,
} from "~/hooks/use-imperative-dialog";
import { trpc } from "~/lib/trpc";
import type { TrafficSource } from "~/routes/(app)/pages/$pageId/variants/$variantId/-components/traffic-sources-table";
import {
  isQueryParam,
  isReferringDomain,
} from "~/routes/(app)/pages/$pageId/variants/$variantId/-utils/traffic-source-validation";

export function EditSourceButton({
  source,
  variantId,
  pageId,
}: {
  source: TrafficSource;
  variantId: string;
  pageId: string;
}) {
  const queryClient = useQueryClient();
  const editDialogRef = useImperativeDialogRef();
  const updateRuleMutation = useMutation(
    trpc.variantTrafficSources.updateSource.mutationOptions({
      onSuccess: () => {
        toast.success("Traffic source updated");
        queryClient.invalidateQueries({
          queryKey: trpc.variantTrafficSources.list.queryKey({
            variantId,
          }),
        });
        queryClient.invalidateQueries({
          queryKey: trpc.variants.getPaginatedVariants.queryKey({
            pageId,
          }),
        });
      },
    }),
  );

  async function onSave(sourceId: string, source: Omit<TrafficSource, "id">) {
    return new Promise<boolean>((resolve) => {
      updateRuleMutation.mutate(
        {
          id: sourceId,
          type: source.type,
          rule: source.rule,
          name: source.name,
          variantId,
          pageId,
        },
        {
          onSuccess: () => {
            editDialogRef.current?.close();
            resolve(true);
          },
        },
      );
    });
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="opacity-0 group-hover/row:opacity-100 transition-opacity hover:bg-gray-200/75 h-[33px] w-[33px]"
        onClick={() => editDialogRef.current?.open()}
      >
        <Pencil size={11} />
      </Button>
      <EditSourcesDialog
        ref={editDialogRef}
        onSave={onSave}
        isSaving={updateRuleMutation.isPending}
        source={source}
      />
    </>
  );
}

type EditSourcesDialogProps = {
  ref: React.RefObject<ImperativeDialogRef | null>;
  onSave: (
    sourceId: string,
    source: Omit<TrafficSource, "id">,
  ) => Promise<boolean>;
  isSaving: boolean;
  source: TrafficSource;
};

const editSourceSchema = z.discriminatedUnion("type", [
  z.object({
    name: z.string().min(1, "Name is required"),
    type: z.literal("query-param"),
    rule: z.string().min(1, "Rule is required").refine(isQueryParam, {
      message:
        "Invalid query parameter format. Expected format: ?param=value or &param=value",
    }),
  }),
  z.object({
    name: z.string().min(1, "Name is required"),
    type: z.literal("referrer"),
    rule: z.string().min(1, "Rule is required").refine(isReferringDomain, {
      message: "Invalid domain format",
    }),
  }),
  z.object({
    name: z.string().min(1, "Name is required"),
    type: z.literal("google-ads"),
    rule: z.string().min(1, "Keyword is required"),
  }),
]);

function EditSourcesDialog({
  ref,
  onSave,
  isSaving,
  source,
}: EditSourcesDialogProps) {
  const { isOpen, setIsOpen } = useImperativeDialog(ref);
  const form = useForm<Omit<TrafficSource, "id">>({
    defaultValues: {
      name: source.name,
      rule: source.rule,
      type: source.type,
    },
    resolver: zodResolver(editSourceSchema),
  });

  useEffect(() => {
    form.reset({
      name: source.name,
      rule: source.rule,
      type: source.type,
    });
  }, [source]);

  const type = form.watch("type");

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogTitle>{getTitle(type)}</DialogTitle>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(async (data) => {
              const success = await onSave(source.id, data);
              if (success) {
                form.reset();
              }
            })}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="rule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {type === "query-param"
                      ? "Query Parameter"
                      : type === "referrer"
                        ? "Referrer"
                        : "Keyword"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={
                        form.watch("type") === "query-param"
                          ? "utm_source=email-campaign"
                          : "example.com"
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>

              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function getTitle(type: "google-ads" | "referrer" | "query-param") {
  switch (type) {
    case "google-ads":
      return "Edit Google Ads traffic source";
    case "referrer":
      return "Edit referring page traffic source";
    case "query-param":
      return "Edit query param traffic source";
  }
}
